import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { Mail, Phone, MapPin, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../utils/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const contactFormText = {
  en: { successTitle: 'Message Sent', successDesc: "Thank you for reaching out. We'll respond within 24 hours.", errorGeneric: 'Something went wrong. Please try again.', required: 'This field is required.', invalidEmail: 'Please enter a valid email address.', sendAnother: 'Send another message' },
  es: { successTitle: 'Mensaje Enviado', successDesc: 'Gracias por contactarnos. Te responderemos dentro de 24 horas.', errorGeneric: 'Algo salió mal. Por favor intenta de nuevo.', required: 'Este campo es obligatorio.', invalidEmail: 'Ingrese un correo electrónico válido.', sendAnother: 'Enviar otro mensaje' },
  pt: { successTitle: 'Mensagem Enviada', successDesc: 'Obrigado por entrar em contato. Retornaremos em até 24 horas.', errorGeneric: 'Algo deu errado. Por favor tente novamente.', required: 'Este campo é obrigatório.', invalidEmail: 'Insira um endereço de e-mail válido.', sendAnother: 'Enviar outra mensagem' },
  fr: { successTitle: 'Message Envoyé', successDesc: 'Merci de nous avoir contactés. Nous répondrons dans les 24 heures.', errorGeneric: 'Une erreur est survenue. Veuillez réessayer.', required: 'Ce champ est requis.', invalidEmail: 'Veuillez entrer une adresse e-mail valide.', sendAnother: 'Envoyer un autre message' },
  it: { successTitle: 'Messaggio Inviato', successDesc: 'Grazie per averci contattato. Risponderemo entro 24 ore.', errorGeneric: 'Qualcosa è andato storto. Per favore riprova.', required: 'Questo campo è obbligatorio.', invalidEmail: 'Inserisci un indirizzo email valido.', sendAnother: 'Invia un altro messaggio' },
};

const Contact = () => {
  useDocumentMeta({
    title: 'Contact | Revs Vibes',
    description: 'Get in touch with Revs Vibes for collector car advisory, sourcing, selling, and private consultations.',
    canonical: 'https://www.revsvibes.com/contact',
    image: 'https://customer-assets.emergentagent.com/job_luxury-motors-13/artifacts/ccrqp8nh_FB_IMG_1770695261526.jpg',
  });
  const { language } = useLanguage();
  const ft = contactFormText[language] || contactFormText.en;
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState('idle'); // idle | success | error
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors(prev => { const n = { ...prev }; delete n[e.target.name]; return n; });
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = ft.required;
    if (!formData.email.trim()) errs.email = ft.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = ft.invalidEmail;
    if (!formData.subject.trim()) errs.subject = ft.required;
    if (!formData.message.trim()) errs.message = ft.required;
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    setErrors({});
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error('Failed to send');
      setFormState('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setFormState('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="contact-hero relative bg-black text-white overflow-hidden h-[500px] sm:h-[550px] md:h-[650px] lg:h-[700px] clickable-card">
        <div className="absolute inset-0">
          <picture>
            <source
              media="(max-width: 767px) and (orientation: portrait)"
              srcSet="https://customer-assets.emergentagent.com/job_f184c80b-9543-4a62-923b-8fe110e27c5d/artifacts/rdb66c0h_image.png"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_f184c80b-9543-4a62-923b-8fe110e27c5d/artifacts/1q4z01h6_image.png"
              alt="Mercedes-Benz 300SL Gullwing"
              className="w-full h-full object-cover opacity-40 contact-hero-img"
              style={{ objectPosition: 'center 55%' }}
            />
          </picture>
        </div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold mb-4">{getTranslation(language, 'contact.title')}</h1>
            <p className="text-base sm:text-lg text-gray-300">{getTranslation(language, 'contact.subtitle')}</p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[11px] sm:text-xs tracking-[0.24em] font-light text-center mb-14" style={{ color: '#a8adb5' }}>
            {language === 'en' ? 'Access is not requested. It is understood.' :
             language === 'es' ? 'El acceso no se solicita. Se entiende.' :
             language === 'pt' ? 'O acesso n\u00e3o \u00e9 solicitado. \u00c9 compreendido.' :
             language === 'fr' ? 'L\u2019acc\u00e8s ne se demande pas. Il se comprend.' :
             'L\u2019accesso non si richiede. Si comprende.'}
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-semibold mb-6">
                  {language === 'en' ? 'Contact Information' :
                   language === 'es' ? 'Información de Contacto' :
                   language === 'pt' ? 'Informações de Contato' :
                   language === 'fr' ? 'Informations de Contact' :
                   'Informazioni di Contatto'}
                </h2>
                <p className="text-gray-500 mb-8">
                  {language === 'en' ? "Whether you're looking to buy, sell, or simply learn more about our services, we'd love to hear from you." :
                   language === 'es' ? 'Ya sea que estés buscando comprar, vender o simplemente aprender más sobre nuestros servicios, nos encantaría saber de ti.' :
                   language === 'pt' ? 'Quer você esteja procurando comprar, vender ou simplesmente aprender mais sobre nossos serviços, adoraríamos ouvir de você.' :
                   language === 'fr' ? 'Que vous cherchiez à acheter, vendre ou simplement en savoir plus sur nos services, nous serions ravis de vous entendre.' :
                   'Che tu stia cercando di comprare, vendere o semplicemente saperne di più sui nostri servizi, ci piacerebbe sentirti.'}
                </p>
              </div>

              <Card className="border-none shadow-sm">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">
                        {language === 'en' ? 'Location' :
                         language === 'es' ? 'Ubicación' :
                         language === 'pt' ? 'Localização' :
                         language === 'fr' ? 'Emplacement' :
                         'Posizione'}
                      </h3>
                      <p className="text-gray-500 text-sm" style={{ whiteSpace: 'pre-line' }}>
                        {getTranslation(language, 'footer.location')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">
                        {language === 'en' ? 'Phone' :
                         language === 'es' ? 'Teléfono' :
                         language === 'pt' ? 'Telefone' :
                         language === 'fr' ? 'Téléphone' :
                         'Telefono'}
                      </h3>
                      <p className="text-gray-500 text-sm">+1 (407) 456-3086</p>
                      <p className="text-gray-500 text-xs mt-1">
                        {language === 'en' ? 'Mon-Fri, 9AM-6PM EST' :
                         language === 'es' ? 'Lun-Vie, 9AM-6PM EST' :
                         language === 'pt' ? 'Seg-Sex, 9AM-6PM EST' :
                         language === 'fr' ? 'Lun-Ven, 9h-18h EST' :
                         'Lun-Ven, 9AM-6PM EST'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Email</h3>
                      <p className="text-gray-500 text-sm">info@revsvibes.com</p>
                      <p className="text-gray-500 text-xs mt-1">
                        {language === 'en' ? '24/7 response time' :
                         language === 'es' ? 'Tiempo de respuesta 24/7' :
                         language === 'pt' ? 'Tempo de resposta 24/7' :
                         language === 'fr' ? 'Temps de réponse 24h/24 et 7j/7' :
                         'Tempo di risposta 24/7'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-semibold mb-3">
                  {language === 'en' ? 'Office Hours' :
                   language === 'es' ? 'Horario de Oficina' :
                   language === 'pt' ? 'Horário de Funcionamento' :
                   language === 'fr' ? 'Heures d\'Ouverture' :
                   'Orari di Ufficio'}
                </h3>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex justify-between">
                    <span>
                      {language === 'en' ? 'Monday - Friday' :
                       language === 'es' ? 'Lunes - Viernes' :
                       language === 'pt' ? 'Segunda - Sexta' :
                       language === 'fr' ? 'Lundi - Vendredi' :
                       'Lunedì - Venerdì'}
                    </span>
                    <span className="font-medium">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      {language === 'en' ? 'Saturday' :
                       language === 'es' ? 'Sábado' :
                       language === 'pt' ? 'Sábado' :
                       language === 'fr' ? 'Samedi' :
                       'Sabato'}
                    </span>
                    <span className="font-medium">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      {language === 'en' ? 'Sunday' :
                       language === 'es' ? 'Domingo' :
                       language === 'pt' ? 'Domingo' :
                       language === 'fr' ? 'Dimanche' :
                       'Domenica'}
                    </span>
                    <span className="font-medium">
                      {language === 'en' ? 'By Appointment' :
                       language === 'es' ? 'Con Cita Previa' :
                       language === 'pt' ? 'Com Hora Marcada' :
                       language === 'fr' ? 'Sur Rendez-vous' :
                       'Su Appuntamento'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-none shadow-sm">
                <CardContent className="p-8">
                  {formState === 'success' ? (
                    <div className="text-center py-12" data-testid="contact-success">
                      <div className="w-16 h-16 rounded-full bg-black/5 flex items-center justify-center mx-auto mb-6">
                        <Mail className="text-black" size={24} />
                      </div>
                      <h2 className="text-2xl font-semibold mb-3">{ft.successTitle}</h2>
                      <p className="text-gray-500 mb-8 max-w-sm mx-auto">{ft.successDesc}</p>
                      <button
                        onClick={() => setFormState('idle')}
                        className="text-[13px] text-gray-400 hover:text-gray-700 transition-colors duration-300"
                        data-testid="contact-send-another"
                      >
                        {ft.sendAnother} &rarr;
                      </button>
                    </div>
                  ) : (
                  <>
                  <h2 className="text-2xl font-semibold mb-6">
                    {language === 'en' ? 'Send us a message' :
                     language === 'es' ? 'Envíanos un mensaje' :
                     language === 'pt' ? 'Envie-nos uma mensagem' :
                     language === 'fr' ? 'Envoyez-nous un message' :
                     'Inviaci un messaggio'}
                  </h2>
                  {formState === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-sm px-4 py-3 mb-6 text-sm text-red-700" data-testid="contact-error-banner">
                      {ft.errorGeneric}
                    </div>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium mb-2">
                          {getTranslation(language, 'contact.name')} *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className={`w-full ${errors.name ? 'border-red-400 focus:ring-red-400' : ''}`}
                          data-testid="contact-name-input"
                        />
                        {errors.name && <p className="text-xs text-red-500 mt-1" data-testid="contact-name-error">{errors.name}</p>}
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-2">
                          {getTranslation(language, 'contact.email')} *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          className={`w-full ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
                          data-testid="contact-email-input"
                        />
                        {errors.email && <p className="text-xs text-red-500 mt-1" data-testid="contact-email-error">{errors.email}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium mb-2">
                          {getTranslation(language, 'contact.phone')}
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 (305) 555-0123"
                          className="w-full"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium mb-2">
                          {getTranslation(language, 'contact.subject')} *
                        </label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder={language === 'en' ? 'Car Inquiry' :
                                      language === 'es' ? 'Consulta de Auto' :
                                      language === 'pt' ? 'Consulta de Carro' :
                                      language === 'fr' ? 'Demande de Voiture' :
                                      'Richiesta Auto'}
                          className={`w-full ${errors.subject ? 'border-red-400 focus:ring-red-400' : ''}`}
                          data-testid="contact-subject-input"
                        />
                        {errors.subject && <p className="text-xs text-red-500 mt-1" data-testid="contact-subject-error">{errors.subject}</p>}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2">
                        {getTranslation(language, 'contact.message')} *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={language === 'en' ? 'Tell us about your dream car or how we can help you...' :
                                    language === 'es' ? 'Cuéntanos sobre tu auto soñado o cómo podemos ayudarte...' :
                                    language === 'pt' ? 'Conte-nos sobre o carro dos seus sonhos ou como podemos ajudá-lo...' :
                                    language === 'fr' ? 'Parlez-nous de la voiture de vos rêves ou de la façon dont nous pouvons vous aider...' :
                                    'Raccontaci dell\'auto dei tuoi sogni o di come possiamo aiutarti...'}
                        rows={6}
                        className={`w-full ${errors.message ? 'border-red-400 focus:ring-red-400' : ''}`}
                        data-testid="contact-message-input"
                      />
                      {errors.message && <p className="text-xs text-red-500 mt-1" data-testid="contact-message-error">{errors.message}</p>}
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-black text-white hover:bg-gray-800"
                      disabled={submitting}
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? (
                        <Loader2 className="animate-spin mr-2" size={20} />
                      ) : null}
                      {getTranslation(language, 'contact.send')}
                    </Button>
                  </form>
                  </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;